import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { apiRequest } from 'src/helpers/apiRequest';
import { useAppConfig } from 'src/hooks';
import {
  EvaluationsRequest,
  EvaluationsResponse,
} from 'src/types/rest/managementPortalApi';

interface UseEvaluationsProps extends EvaluationsRequest {
  options?: Partial<UseQueryOptions<EvaluationsResponse>>;
}

export const useEvaluations = ({
  evaluations,
  options,
  subject,
}: UseEvaluationsProps) => {
  const config = useAppConfig();
  const {
    managementPortalApi: { apiHost },
  } = config!;

  return useQuery({
    queryFn: () =>
      apiRequest<EvaluationsResponse, EvaluationsRequest>({
        data: { evaluations, subject },
        method: 'POST',
        url: `${apiHost}/access/evaluations`,
      }),
    queryKey: ['evaluations', evaluations],
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    retry: (failureCount, error) => {
      /**
       * No retries for auth issues
       *
       * On 401/403, React Query stops retrying and the auth error propagates
       * to `GlobalEvaluationsContext` where it triggers a sign-in redirect
       * via `AppRouterWithMetadata`, instead of showing a 404 page.
       */
      if (
        error instanceof Response &&
        (error.status === 401 || error.status === 403)
      ) {
        return false;
      }
      // Retry for other errors
      return failureCount < 2;
    },
    staleTime: 2 * 60 * 1000, // Consider fresh for 2 minutes
    ...options,
  });
};
